import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

let youTubeApiPromise = null;

const waitForYouTubeApi = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube API requires a browser environment'));
  }

  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }

  if (youTubeApiPromise) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    const timeoutId = window.setTimeout(() => {
      reject(new Error('YouTube Iframe API load timeout'));
    }, 15000);

    const done = () => {
      if (window.YT && window.YT.Player) {
        window.clearTimeout(timeoutId);
        resolve(window.YT);
      }
    };

    const originalReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof originalReady === 'function') {
        originalReady();
      }
      done();
    };

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Failed to load YouTube Iframe API script'));
      };
      document.head.appendChild(script);
    }

    const poll = window.setInterval(() => {
      if (window.YT && window.YT.Player) {
        window.clearInterval(poll);
        done();
      }
    }, 100);
  });

  return youTubeApiPromise;
};

const formatSeconds = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

export const useYouTubeAudioPlayer = ({ tracks = [], initialTrackId = null, initialVolume = 75 }) => {
  const playerHostRef = useRef(null);
  const playerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const pendingStartRef = useRef(null);
  const playTrackAtIndexRef = useRef(() => {});
  const currentIndexRef = useRef(-1);
  const tracksRef = useRef([]);

  const safeTracks = useMemo(() => tracks.filter(Boolean), [tracks]);
  const initialIndex = useMemo(() => {
    if (!safeTracks.length) return -1;
    if (initialTrackId == null) return 0;
    const idx = safeTracks.findIndex((track) => track.id === initialTrackId);
    return idx >= 0 ? idx : 0;
  }, [safeTracks, initialTrackId]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(initialVolume);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackError, setPlaybackError] = useState('');

  const currentTrack = currentIndex >= 0 ? safeTracks[currentIndex] : null;

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    tracksRef.current = safeTracks;
  }, [safeTracks]);

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const syncProgress = useCallback(() => {
    const player = playerRef.current;
    if (!player || typeof player.getCurrentTime !== 'function') return;

    const nextTime = Number(player.getCurrentTime()) || 0;
    const nextDuration = Number(player.getDuration()) || 0;
    setCurrentTime(nextTime);
    setDuration(nextDuration);
  }, []);

  const playTrackAtIndex = useCallback((index, autoplay = true) => {
    if (!safeTracks.length) return;

    const boundedIndex = Math.max(0, Math.min(index, safeTracks.length - 1));
    const track = safeTracks[boundedIndex];
    const player = playerRef.current;

    setCurrentIndex(boundedIndex);
    setPlaybackError('');

    if (!track?.youtubeId) {
      setIsPlaying(false);
      setPlaybackError('Source offline: no mirror ID for this file.');
      return;
    }

    if (!player || !isReady) {
      pendingStartRef.current = { index: boundedIndex, autoplay };
      return;
    }

    try {
      if (autoplay) {
        player.loadVideoById(track.youtubeId, 0, 'small');
      } else {
        player.cueVideoById(track.youtubeId, 0, 'small');
      }
      setCurrentTime(0);
      setDuration(0);
      if (!autoplay) setIsPlaying(false);
    } catch (error) {
      setPlaybackError('Playback error: this track mirror failed to load.');
      setIsPlaying(false);
    }
  }, [isReady, safeTracks]);

  useEffect(() => {
    playTrackAtIndexRef.current = playTrackAtIndex;
  }, [playTrackAtIndex]);

  const nextTrack = useCallback(() => {
    if (!safeTracks.length) return;
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % safeTracks.length : 0;
    playTrackAtIndex(nextIndex, true);
  }, [currentIndex, playTrackAtIndex, safeTracks.length]);

  const prevTrack = useCallback(() => {
    if (!safeTracks.length) return;
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : safeTracks.length - 1;
    playTrackAtIndex(prevIndex, true);
  }, [currentIndex, playTrackAtIndex, safeTracks.length]);

  const togglePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (!player || !isReady) return;

    if (!currentTrack?.youtubeId) {
      setPlaybackError('Source offline: no mirror ID for this file.');
      return;
    }

    if (isPlaying) {
      player.pauseVideo();
    } else {
      const state = player.getPlayerState();
      if (state === window.YT?.PlayerState?.UNSTARTED || state === window.YT?.PlayerState?.CUED) {
        playTrackAtIndex(currentIndex >= 0 ? currentIndex : 0, true);
      } else {
        player.playVideo();
      }
    }
  }, [currentIndex, currentTrack, isPlaying, isReady, playTrackAtIndex]);

  const stopPlayback = useCallback(() => {
    const player = playerRef.current;
    if (!player || !isReady) return;

    player.pauseVideo();
    player.seekTo(0, true);
    setIsPlaying(false);
    setCurrentTime(0);
  }, [isReady]);

  const seekToRatio = useCallback((ratio) => {
    const player = playerRef.current;
    if (!player || !isReady || !duration) return;
    const bounded = Math.max(0, Math.min(1, ratio));
    player.seekTo(duration * bounded, true);
    setCurrentTime(duration * bounded);
  }, [duration, isReady]);

  const selectTrackById = useCallback((trackId) => {
    const index = safeTracks.findIndex((track) => track.id === trackId);
    if (index >= 0) {
      playTrackAtIndex(index, true);
    }
  }, [playTrackAtIndex, safeTracks]);

  const setVolume = useCallback((nextVolume) => {
    const numeric = Number(nextVolume);
    const bounded = Math.max(0, Math.min(100, Number.isFinite(numeric) ? numeric : 0));
    setVolumeState(bounded);
    if (playerRef.current && isReady) {
      playerRef.current.setVolume(bounded);
    }
  }, [isReady]);

  useEffect(() => {
    let cancelled = false;

    waitForYouTubeApi()
      .then((YT) => {
        if (cancelled || !playerHostRef.current) return;

        playerRef.current = new YT.Player(playerHostRef.current, {
          width: '1',
          height: '1',
          videoId: '',
          playerVars: {
            autoplay: 0,
            controls: 0,
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            fs: 0,
            disablekb: 0,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              event.target.setVolume(initialVolume);
              setIsReady(true);

              if (pendingStartRef.current) {
                const { index, autoplay } = pendingStartRef.current;
                pendingStartRef.current = null;
                playTrackAtIndexRef.current(index, autoplay);
              }
            },
            onStateChange: (event) => {
              const state = event.data;
              const ytState = window.YT?.PlayerState;
              if (state === ytState?.PLAYING) {
                setIsPlaying(true);
                syncProgress();
              } else if (state === ytState?.PAUSED || state === ytState?.CUED) {
                setIsPlaying(false);
                syncProgress();
              } else if (state === ytState?.ENDED) {
                setIsPlaying(false);
                setCurrentTime(0);
                const tracksNow = tracksRef.current;
                if (tracksNow.length) {
                  const nextIndex = currentIndexRef.current >= 0
                    ? (currentIndexRef.current + 1) % tracksNow.length
                    : 0;
                  playTrackAtIndexRef.current(nextIndex, true);
                }
              }
            },
            onError: () => {
              setPlaybackError('Playback error: this track mirror failed to load.');
              setIsPlaying(false);
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPlaybackError('YouTube audio service unavailable right now.');
        }
      });

    return () => {
      cancelled = true;
      clearProgressTimer();
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        playerRef.current.destroy();
      }
      playerRef.current = null;
    };
  }, [clearProgressTimer, initialVolume, syncProgress]);

  useEffect(() => {
    clearProgressTimer();
    if (isPlaying) {
      progressTimerRef.current = window.setInterval(syncProgress, 500);
    }

    return () => {
      clearProgressTimer();
    };
  }, [clearProgressTimer, isPlaying, syncProgress]);

  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return {
    playerHostRef,
    currentTrack,
    currentIndex,
    isReady,
    isPlaying,
    volume,
    currentTime,
    duration,
    progressPercent,
    playbackError,
    formattedTime: `${formatSeconds(currentTime)} / ${formatSeconds(duration)}`,
    selectTrackById,
    playTrackAtIndex,
    togglePlayPause,
    stopPlayback,
    nextTrack,
    prevTrack,
    seekToRatio,
    setVolume,
  };
};

export default useYouTubeAudioPlayer;
